import { http, HttpResponse, HttpResponseResolver } from 'msw';

import { estonianInsuranceCompanies } from '@main/mocks-copy/data/companyStatus/companyStatusEE';
import {
  CarQuotePayload,
  Country,
  CoveragePolicyData,
  InsurancePackageName,
  InsuranceSubType,
} from '@main/types';
import { PackageScopeRequest } from '@main/types/insurance/EquivalentPackageScope';
import { getDevToolsLocalItem } from '@main/utils/devTools';

import { getData } from './data/collectData';
import { danishInsuranceCompanies } from './data/companyStatus/companyStatusDK';
import { frenchInsuranceCompanies } from './data/companyStatus/companyStatusFR';
import { pensionCompanies, swedishInsuranceCompanies } from './data/companyStatus/companyStatusSE';
import { getEquivalentPackageScope, getParameters } from './data/parameters';
import { getPriceEstimateSE } from './data/priceEstimate/priceEstimateSE';
import { mockDb } from './mockDb';

const getDataHandler: HttpResponseResolver = ({ params }) => {
  const collectionId = params.id as string;
  const collection = mockDb.collection.findFirst({ where: { id: { equals: collectionId } } });
  return HttpResponse.json(getData(collection), { status: 200 });
};

export const handlers = [
  http.get<never, never, Company[]>(`${API_URL}/insurance-company/status`, () => {
    const sharedData = getDevToolsLocalItem({ key: 'localSharedData' });

    if (sharedData?.clientConfig.market.country === Country.dk) {
      return HttpResponse.json(danishInsuranceCompanies, { status: 200 });
    }

    if (sharedData?.clientConfig.market.country === Country.fr) {
      return HttpResponse.json(frenchInsuranceCompanies, { status: 200 });
    }

    if (sharedData?.clientConfig.market.country === Country.ee) {
      return HttpResponse.json(estonianInsuranceCompanies, { status: 200 });
    }

    return HttpResponse.json(
      sharedData?.clientConfig.clientId.includes('pension')
        ? pensionCompanies
        : swedishInsuranceCompanies,
      { status: 200 },
    );
  }),
  http.get(`${API_URL}/insurance-collection/get-data/:id`, getDataHandler),
  http.get(`${API_URL}/pension-collection/get-data/:id`, getDataHandler),

  http.post<
    never,
    {
      insuranceSubType: InsuranceSubType;
      insuranceCompany: string;
      insuranceName: InsurancePackageName;
    }
  >(`${API_URL}/insurance-collection/get-parameters/`, async ({ request }) => {
    const { insuranceSubType, insuranceCompany, insuranceName } = await request.json();
    return HttpResponse.json(getParameters({ insuranceSubType, insuranceCompany, insuranceName }));
  }),

  http.post<
    never,
    {
      coveragePolicyData: CoveragePolicyData;
      packageScope: PackageScopeRequest;
    }
  >(`${API_URL}/coverage/equivalent-package-scopes`, async ({ request }) => {
    const { packageScope, coveragePolicyData } = await request.json();

    return HttpResponse.json(getEquivalentPackageScope({ coveragePolicyData, packageScope }));
  }),

  // Car ICA endpoint
  http.post<never, CarQuotePayload>(
    `${API_URL}/checkout/quote/price-estimate`,
    async ({ request }) => HttpResponse.json(getPriceEstimateSE(await request.json())),
  ),
];
